import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  Stack,
  Avatar,
  CircularProgress,
  Fade,
  Zoom,
} from "@mui/material";
import {
  CheckCircleRounded,
  ErrorRounded,
  WarningAmberRounded,
  HourglassTopRounded,
  SettingsRounded,
} from "@mui/icons-material";

const statusStyles = {
  success: {
    bg: "#ecfdf3",
    border: "#bbf7d0",
    title: "#166534",
    desc: "#166534",
    iconBg: "#16a34a",
    icon: <CheckCircleRounded />,
  },
  loading: {
    bg: "#eff6ff",
    border: "#bfdbfe",
    title: "#1e3a8a",
    desc: "#475569",
    iconBg: "#2563eb",
    icon: <CircularProgress size={18} thickness={5} color="inherit" />,
  },
  warning: {
    bg: "#fff7ed",
    border: "#fed7aa",
    title: "#92400e",
    desc: "#92400e",
    iconBg: "#f59e0b",
    icon: <WarningAmberRounded />,
  },
  pending: {
    bg: "#f8fafc",
    border: "#cbd5e1",
    title: "#334155",
    desc: "#475569",
    iconBg: "#64748b",
    icon: <HourglassTopRounded />,
  },
  error: {
    bg: "#fef2f2",
    border: "#fecaca",
    title: "#991b1b",
    desc: "#991b1b",
    iconBg: "#dc2626",
    icon: <ErrorRounded />,
  },
};

function ResourceStatusCard({ status = "pending", description }) {
  const cfg = statusStyles[status] || statusStyles.pending;

  return (
    <Zoom in timeout={400}>
     <Box
  sx={{
    p: 2,
    mb: 1.8,
    borderRadius: 3,
    background: (theme) =>
      theme.palette.mode === "dark" ? (cfg.dark?.bg ?? cfg.bg) : (cfg.light?.bg ?? cfg.bg),
    border: (theme) =>
      `1px solid ${
        theme.palette.mode === "dark" ? (cfg.dark?.border ?? cfg.border) : (cfg.light?.border ?? cfg.border)
      }`,
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "none"
        : "0 8px 24px rgba(15,23,42,0.04)",
  }}
>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 44, height: 44, bgcolor: cfg.iconBg, color: "#fff" }}>
            {cfg.icon}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, 
                color: cfg.title, 
                lineHeight: 1.2 }}
            >
              Resource Creation
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: cfg.desc, lineHeight: 1.45 }}
            >
              {description || "Waiting to start..."}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Zoom>
  );
}

export default function AzureSubmissionProgressPopup({
  open,
  progress = 0,
  steps = {},
  successMessage,
}) {
  const provisioningStatus = steps.provisioning?.status || "pending";
  const safeProgress = Math.max(0, Math.min(100, progress));

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
          boxShadow: "0 30px 80px rgba(15,23,42,.18)",
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Fade in timeout={500}>
          <Stack spacing={1.4} alignItems="center" sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                color: "#fff",
                fontSize: 34,
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                boxShadow: "0 20px 35px rgba(99,102,241,.25)",
              }}
            >
              <SettingsRounded sx={{ color: "#fff", fontSize: 34 }} />
            </Box>

           <Typography
  variant="h5"
  sx={{
    fontWeight: 800,
    color: "text.primary",
    textAlign: "center",
  }}
>
  Processing Request
</Typography>

<Typography
  variant="body2"
  sx={{
    color: "text.secondary",
    textAlign: "center",
    maxWidth: 420,
  }}
>
  Please wait while we create your resource request.
</Typography>

          </Stack>
        </Fade>

        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
              Overall Progress
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, color: "#4f46e5" }}>
              {safeProgress}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={safeProgress}
            sx={{
              height: 10,
              borderRadius: 999,
              backgroundColor: "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
              },
            }}
          />
        </Box>

        <ResourceStatusCard
          status={provisioningStatus}
          description={steps.provisioning?.desc}
        />

        {successMessage && provisioningStatus === "success" && (
          <Fade in timeout={600}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 3,
                backgroundColor: "#ecfdf3",
                border: "1px solid #bbf7d0",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#166534" }}>
                {successMessage}
              </Typography>
            </Box>
          </Fade>
        )}

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, textAlign: "center", color: "text.disabled" }}
        >
          Please do not close this window while processing.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
